<template>
  <aside class="ai-sidebar-shell d-flex flex-column h-100 border-start bg-white overflow-hidden">
    <div class="ai-sidebar-header d-flex align-items-center justify-content-between px-3 py-3 border-bottom">
      <div class="d-flex align-items-center gap-2 min-w-0">
        <span class="ai-sidebar-icon rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0">
          <i class="bi bi-stars"></i>
        </span>
        <span class="ai-sidebar-title text-truncate fw-semibold">Complyon AI</span>
      </div>

      <span class="badge rounded-pill bg-light text-secondary border border-light flex-shrink-0">LIVE</span>
    </div>

    <div class="ai-sidebar-scroll flex-grow-1 overflow-auto px-3 py-3">
      <div class="mb-3">
        <div class="ai-sidebar-kicker text-uppercase">Briefing</div>
        <div class="ai-sidebar-subtitle mt-1">This week's compliance posture</div>
      </div>

      <section class="ai-briefing-card rounded-4 text-white p-3">
        <p class="mb-3">
          Procurement and finance remain the highest-risk areas this week.
        </p>

        <p class="mb-0">
          Three issues are blocking actions right now:
        </p>

        <ol class="ai-number-list mt-1 mb-3 ps-3">
          <li>ABC Tech supplier tax certificate has expired.</li>
          <li>Invoice INV-2219 cannot pass 3-way match.</li>
          <li>Platform support agreement is over the R 500 000 CFO threshold.</li>
        </ol>

        <p class="mb-3">
          Recommended: hold the two payments, request fresh evidence from ABC Tech, and route the contract to the CFO with the DoA exception note.
        </p>

        <div class="ai-briefing-meta small text-light-emphasis">
          ↳ Sources · 4 controls · 3 evidence items · 1 policy
        </div>
      </section>

      <div class="mt-3">
        <div class="ai-sidebar-kicker text-uppercase">Ask Compliance</div>

        <div class="d-grid gap-2 mt-2">
          <button
            v-for="question in questions"
            :key="question"
            type="button"
            class="btn btn-outline-light ai-question-btn text-start"
          >
            <span class="d-inline-flex align-items-center gap-2">
              <span class="ai-question-dot rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0">
                <i class="bi bi-circle"></i>
              </span>
              <span>{{ question }}</span>
            </span>
          </button>
        </div>

        <div class="mt-2">
          <textarea
            class="form-control ai-prompt-input"
            rows="3"
            placeholder="Ask anything about your compliance posture..."
          ></textarea>
          <button type="button" class="ai-send-btn btn btn-dark">
            <i class="bi bi-send"></i>
          </button>
        </div>
      </div>

      <div class="mt-4">
        <div class="ai-sidebar-kicker text-uppercase">Recent evidence</div>

        <div class="d-grid gap-2 mt-2">
          <article
            v-for="item in recent_evidence"
            :key="item.title"
            class="ai-evidence-item d-flex align-items-start justify-content-between gap-3"
          >
            <div class="d-flex align-items-start gap-2 min-w-0">
              <span :class="['ai-evidence-icon d-inline-flex align-items-center justify-content-center flex-shrink-0', item.icon_class]">
                <i class="bi" :class="item.icon"></i>
              </span>
              <div class="min-w-0">
                <div class="ai-evidence-title text-truncate">{{ item.title }}</div>
                <div class="ai-evidence-meta text-truncate">{{ item.meta }}</div>
              </div>
            </div>

            <span class="badge rounded-pill fw-semibold text-nowrap" :class="item.badge_class">
              {{ item.badge }}
            </span>
          </article>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'ComplyonAiSidebar',
  data: () => ({
    questions: [
      'What evidence is missing for ABC Tech?',
      'Can INV-2219 be paid?',
      'Who must approve this contract?',
      'Which controls failed this month?',
      'What are the POPIA gaps?',
      'Generate audit pack preview'
    ],
    recent_evidence: [
      {
        title: 'Tax certificate',
        meta: 'ABC Tech - expired',
        badge: 'Expired',
        badge_class: 'bg-danger-subtle text-danger-emphasis',
        icon: 'bi-x-circle',
        icon_class: 'is-danger'
      },
      {
        title: 'Delivery note',
        meta: 'Invoice INV-2219 missing',
        badge: 'Missing',
        badge_class: 'bg-warning-subtle text-warning-emphasis',
        icon: 'bi-x-circle',
        icon_class: 'is-danger'
      },
      {
        title: 'Admin access review',
        meta: 'Control evidence overdue',
        badge: 'Overdue',
        badge_class: 'bg-warning-subtle text-warning-emphasis',
        icon: 'bi-exclamation-circle',
        icon_class: 'is-warning'
      }
    ]
  })
};
</script>

<style scoped>
.ai-sidebar-shell {
  width: 22rem;
  min-width: 22rem;
  max-width: 22rem;
  background: #fff;
}

.ai-sidebar-header {
  min-height: 4.25rem;
}

.ai-sidebar-icon {
  width: 2rem;
  height: 2rem;
  background: #edf2f7;
  color: #334155;
}

.ai-sidebar-title {
  color: #0f172a;
}

.ai-sidebar-kicker {
  color: #60718a;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
}

.ai-sidebar-subtitle {
  color: #16314b;
  font-size: 0.92rem;
}

.ai-sidebar-scroll {
  scrollbar-width: thin;
  scrollbar-color: #d2d9e2 transparent;
}

.ai-sidebar-scroll::-webkit-scrollbar {
  width: 6px;
}

.ai-sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.ai-sidebar-scroll::-webkit-scrollbar-thumb {
  background-color: #d2d9e2;
  border-radius: 999px;
}

.ai-briefing-card {
  background: var(--primary-color);
  line-height: 1.5;
  box-shadow: 0 18px 44px rgb(65 111 110 / 18%);
}

.ai-number-list {
  line-height: 1.5;
}

.ai-briefing-meta {
  color: rgb(255 255 255 / 78%);
}

.ai-question-btn {
  border-color: #dbe4ee;
  color: #16253b;
  border-radius: 0.65rem;
  padding: 0.8rem 0.9rem;
  background: #fff;
}

.ai-question-btn:hover,
.ai-question-btn:focus {
  background: #f8fbff;
  border-color: #cfd9e6;
  box-shadow: none;
}

.ai-question-dot {
  width: 1rem;
  height: 1rem;
  color: #6b7280;
}

.ai-prompt-input {
  min-height: 5.8rem;
  resize: none;
  border-color: #dbe4ee;
  box-shadow: none !important;
}

.ai-prompt-input:focus {
  border-color: #b9c6d6;
}

.ai-send-btn {
  position: relative;
  float: right;
  margin-top: -2.4rem;
  margin-right: 0.35rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 0.5rem;
  background: #365466;
  border-color: #365466;
}

.ai-evidence-item {
  padding: 0.7rem 0.6rem 0.7rem 0;
  border-bottom: 1px dashed #e6edf4;
}

.ai-evidence-item:last-child {
  border-bottom: 0;
}

.ai-evidence-icon {
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 0.45rem;
  background: #fff1f0;
  color: #d94841;
}

.ai-evidence-icon.is-warning {
  background: #fff3e4;
  color: #dc7a12;
}

.ai-evidence-title {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 700;
}

.ai-evidence-meta {
  color: #5f6f83;
  font-size: 0.78rem;
}
</style>
